import { get, isEmpty, findIndex } from 'lodash';
import arrayMove from 'array-move/index';
import { v1 as uuidv1 } from 'uuid';
import {
  SLIDER_DEFAULT_MAX,
  SLIDER_DEFAULT_MIN,
  SLIDER_DEFAULT_STEP,
} from 'containers/QuestionnairePage/constants';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { MIN, MAX, STEP, MAX_ALLOWED_STEPS } from './constants';

export const sanitiseValues = ({
  minValue = SLIDER_DEFAULT_MIN,
  maxValue = SLIDER_DEFAULT_MAX,
  rangeStep = SLIDER_DEFAULT_STEP,
  field,
}) => {
  let newStep = rangeStep;
  if (rangeStep <= 0) {
    newStep = 1;
  }
  const result = (maxValue - minValue) / newStep;
  if (result <= MAX_ALLOWED_STEPS) {
    return {
      minValue,
      maxValue,
      rangeStep: newStep,
    };
  }
  let newMinValue = minValue;
  let newMaxValue = maxValue;
  switch (field) {
    case MIN:
      newMinValue = -(MAX_ALLOWED_STEPS * newStep - newMaxValue);
      break;
    case MAX:
      newMaxValue = MAX_ALLOWED_STEPS * newStep + newMinValue;
      break;
    case STEP:
      newStep = (newMaxValue - newMinValue) / MAX_ALLOWED_STEPS;
      break;
    default:
      break;
  }
  return {
    minValue: newMinValue,
    maxValue: newMaxValue,
    rangeStep: newStep,
  };
};

export const hasOnlyDefaultTicks = (question) => {
  const ticks = get(question, 'slider.ticks', []);
  return ticks.length <= 2 && ticks.every(isTickEmpty);
};
export const isTickEmpty = (tick) => {
  const label = getPromptFromHtml(tick.tickLabel);
  return isEmpty(label);
};

const getTickValues = ({ minValue, maxValue, ticksLength }) => {
  if (ticksLength <= 1) {
    return [minValue];
  }

  if (ticksLength === 2) {
    return [minValue, maxValue];
  }

  const range = maxValue - minValue;
  const middleTicksCount = ticksLength - 2;

  return Array.from(Array(ticksLength), (_, tickIndex) =>
    Math.floor(minValue + (range * tickIndex) / middleTicksCount),
  );
};

export const getTicks = ({ ticks, minValue, maxValue }) => {
  const ticksLength = ticks.length;
  const tickValues = getTickValues({
    minValue,
    maxValue,
    ticksLength,
  });

  return ticks.map((tick, index) => ({
    ...tick,
    tickValue: tickValues[index],
  }));
};

export const getEmptyTick = (valid = false) => ({
  tickLabel: '',
  refId: uuidv1(),
  isEmptyTick: !valid,
});

export const emptyTicksSelector = (ticks) =>
  ticks.filter((tick) => tick.isEmptyTick);

export const activeTicksSelector = (ticks) =>
  ticks.filter((tick) => !tick.isEmptyTick);

export const canDragTick = (tick) => !tick.isEmptyTick;

export const isTickDragEnabled = (ticks) =>
  activeTicksSelector(ticks).length >= 1;

export const moveTickByDirection = ({
  ticks,
  refId,
  direction,
  minValue,
  maxValue,
}) => {
  const emptyTicks = emptyTicksSelector(ticks);
  const activeTicks = activeTicksSelector(ticks);
  const sourceIndex = findIndex(activeTicks, { refId });
  const targetIndex = sourceIndex + direction;

  if (sourceIndex === -1 || targetIndex === sourceIndex) {
    return null;
  }

  if (targetIndex < 0 || targetIndex >= activeTicks.length) {
    return null;
  }

  const reorderedActiveTicks = arrayMove(activeTicks, sourceIndex, targetIndex);

  return getTicks({
    ticks: [...reorderedActiveTicks, ...emptyTicks],
    minValue,
    maxValue,
  });
};

const getTick = (tickValue) => ({
  ...getEmptyTick(true),
  tickValue,
});

const SLIDER_DEFAULT_PROPERTIES = {
  isRangeSelectionVisible: false,
  minValue: SLIDER_DEFAULT_MIN,
  maxValue: SLIDER_DEFAULT_MAX,
  rangeStep: SLIDER_DEFAULT_STEP,
};

export const getTransformedSlider = (slider) => {
  const sliderRefId = get(slider, 'refId', uuidv1());
  let ticks = get(slider, 'ticks', [
    getTick(SLIDER_DEFAULT_MIN),
    getTick(SLIDER_DEFAULT_MAX),
  ]);

  if (slider) {
    const hasEmptyTick = ticks.some((tick) => tick.isEmptyTick);

    ticks = ticks.map((tick) => ({
      ...tick,
      refId: tick.refId || uuidv1(),
    }));

    if (!hasEmptyTick) {
      ticks = [...ticks, getEmptyTick()];
    }
  }

  return {
    ...SLIDER_DEFAULT_PROPERTIES,
    ...slider,
    ticks,
    refId: sliderRefId,
  };
};
